var __extends = (this && this.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (Object.prototype.hasOwnProperty.call(b, p)) d[p] = b[p]; };
        return extendStatics(d, b);
    };
    return function (d, b) {
        if (typeof b !== "function" && b !== null)
            throw new TypeError("Class extends value " + String(b) + " is not a constructor or null");
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
/**
 * @module windy
 */
import ViewHint from 'ol/ViewHint.js';
import WebGLArrayBuffer from 'ol/webgl/Buffer.js';
import WebGLLayerRenderer from 'ol/renderer/webgl/Layer.js';
import { ARRAY_BUFFER, DYNAMIC_DRAW, ELEMENT_ARRAY_BUFFER } from 'ol/webgl.js';
import { AttributeType, DefaultUniform } from 'ol/webgl/Helper.js';
import { create as createTransform } from 'ol/transform.js';
import { createEmpty, equals } from 'ol/extent.js';
import SourceState from 'ol/source/State.js';
var NwpWindyLayerRenderer = /** @class */ (function (_super) {
    __extends(NwpWindyLayerRenderer, _super);
    /**
     * @param {import("../../layer/Layer.js").default} layer Layer.
     * @param {Options} options Options.
    */
    function NwpWindyLayerRenderer(layer, options) {
        var _this = this;
        var uniforms = options.uniforms || {};
        var projectionMatrixTransform = createTransform();
        uniforms[DefaultUniform.PROJECTION_MATRIX] = projectionMatrixTransform;
        _this = _super.call(this, layer, {
            uniforms: uniforms,
            postProcesses: options.postProcesses,
        }) || this;
        _this.verticesBuffer_ = new WebGLArrayBuffer(ARRAY_BUFFER, DYNAMIC_DRAW);
        _this.indicesBuffer_ = new WebGLArrayBuffer(ELEMENT_ARRAY_BUFFER, DYNAMIC_DRAW);
        _this.program_ = _this.helper.getProgram(options.fragmentShader, options.vertexShader);
        /**
         * A list of attributes used by the renderer. By default only the position and
         * index of the vertex (0 to 3) are required.
         * @type {Array<import('../../webgl/Helper.js').AttributeDescription>}
         */
        _this.attributes = [
            {
                name: 'a_position',
                size: 2,
                type: AttributeType.FLOAT,
            },
            {
                name: 'a_offset',
                size: 2,
                type: AttributeType.FLOAT,
            },
            {
                name: 'a_distance',
                size: 1,
                type: AttributeType.FLOAT,
            },
            {
                name: 'a_start',
                size: 1,
                type: AttributeType.FLOAT,
            },
            {
                name: 'a_side',
                size: 1,
                type: AttributeType.FLOAT,
            },
            {
                name: 'a_speed',
                size: 1,
                type: AttributeType.FLOAT,
            }
        ];
        _this.stride_ = 0;
        _this.attributes.forEach(function (att) { return _this.stride_ = _this.stride_ + att.size; });
        _this.previousExtent_ = createEmpty();
        /**
         * This transform is updated on every frame and is the composition of:
         * - invert of the world->screen transform that was used when rebuilding buffers (see `this.renderTransform_`)
         * - current world->screen transform
         * @type {import("../../transform.js").Transform}
         * @private
         */
        _this.currentTransform_ = projectionMatrixTransform;
        /**
         * @type {Float32Array}
         * @private
         */
        /**
         * @protected
         * @type {?import("../../ImageBase.js").default}
         */
        _this.source = null;
        _this.refresh_offset_ = 0;
        _this.density_ = layer.getDensity();
        _this.paths_ = new Array(_this.density_);
        _this.thick_ = layer.getThick();
        _this.uu_ = new Float32Array(0);
        _this.vv_ = new Float32Array(0);
        _this.x_offset_ = 5000;
        _this.y_offset_ = 5000;
        _this.width_ = -1;
        _this.height_ = -1;
        _this.propertyChanged_ = true;
        return _this;
    }
    NwpWindyLayerRenderer.prototype.setDensity = function (density) {
        this.paths_ = null;
        this.paths_ = new Array(density);
        this.propertyChanged_ = true;
    };
    NwpWindyLayerRenderer.prototype.setThick = function (thick) {
        this.thick_ = thick;
        this.propertyChanged_ = true;
    };
    /**
     * Render the layer.
     * @param {import("../../PluggableMap.js").FrameState} frameState Frame state.
     * @return {HTMLElement} The rendered element.
     */
    NwpWindyLayerRenderer.prototype.renderFrame = function (frameState) {
        var st = new Date().getTime();
        var renderCount = this.indicesBuffer_.getSize();
        this.helper.drawElements(0, renderCount);
        this.helper.finalizeDraw(frameState);
        var canvas = this.helper.getCanvas();
        var layerState = frameState.layerStatesArray[frameState.layerIndex];
        var opacity = layerState.opacity;
        if (opacity !== parseFloat(canvas.style.opacity)) {
            canvas.style.opacity = String(opacity);
        }
        // console.log(` renderFrame end(10) -> ${(new Date().getTime()) - st}`);
        return canvas;
    };
    /**
     * Determine whether render should be called.
     * @param {import("../../PluggableMap.js").FrameState} frameState Frame state.
     * @return {boolean} Layer is ready to be rendered.
     */
    NwpWindyLayerRenderer.prototype.prepareFrame = function (frameState) {
        var st = new Date().getTime();
        // console.log(` WindyLayerRenderer -> prepareFrame begin ${new Date().getTime()}`);
        var layer = this.getLayer();
        var viewState = frameState.viewState;
        var source = layer.getSource();
        var viewNotMoving = !frameState.viewHints[ViewHint.ANIMATING] &&
            !frameState.viewHints[ViewHint.INTERACTING];
        var extentChanged = !equals(this.previousExtent_, frameState.extent);
        var changeSource = false;
        if (this.source_ != source) {
            if (source.getState() != SourceState.READY) {
                return false;
            }
            else {
                this.source_ = source;
            }
            var data = source.loadNwpData();
            if (data == null) {
                return false;
            }
            else {
                var length_1 = data.length;
                this.uu_ = new Float32Array(length_1 / 2);
                this.vv_ = new Float32Array(length_1 / 2);
                for (var i = 0; i < length_1; i += 2) {
                    this.uu_[i] = data[i];
                    this.vv_[i] = data[i + 1];
                }
            }
            changeSource = true;
        }
        if (((viewNotMoving && extentChanged) || changeSource || this.propertyChanged_) && (this.uu_ !== undefined && this.vv_ !== undefined)) {
            this.propertyChanged_ = false;
            this.rebuildBuffers_(frameState);
            this.previousExtent_ = frameState.extent.slice();
        }
        // apply the current projection transform with the invert of the one used to fill buffers
        this.helper.makeProjectionTransform(frameState, this.currentTransform_);
        this.helper.useProgram(this.program_);
        this.helper.prepareDraw(frameState);
        // write new data
        this.helper.bindBuffer(this.verticesBuffer_);
        this.helper.bindBuffer(this.indicesBuffer_);
        this.helper.enableAttributes(this.attributes);
        // console.log(` prepareFrame end(10) -> ${(new Date().getTime()) - st}`);
        return true;
    };
    /**
    * Rebuild internal webgl buffers based on current view extent; costly, should not be called too much
    * @param {import("../../PluggableMap").FrameState} frameState Frame state.
    * @private
    */
    NwpWindyLayerRenderer.prototype.rebuildBuffers_ = function (frameState) {
        var st = new Date().getTime();
        // console.log(` WindyLayerRenderer -> rebuildBuffers_ begin (1)`);
        var viewState = frameState.viewState;
        var resolution = viewState.resolution;
        var extent = this.getLayer().getExtent();
        // saves the projection transform for the current frame state
        var projectionTransform = createTransform();
        this.helper.makeProjectionTransform(frameState, projectionTransform);
        var extents = frameState.extent;
        var size = frameState.size;
        var minx = extents[0], miny = extents[1], maxx = extents[2], maxy = extents[3];
        var width = this.width_; // , height = this.height_;
        var x_offset = this.x_offset_, y_offset = this.y_offset_;
        var stepx = x_offset < resolution ? x_offset / 2 : resolution;
        var stepy = y_offset < resolution ? y_offset / 2 : resolution;
        var pathMaxLength = resolution * 150;
        // loop on features to fill the buffer
        var elementsIndex = [], speeds = [], starts = [], paths = [], totalLength = 0;
        for (var i = 0; i < this.paths_.length; i++) {
            var path = { start: Math.random() * pathMaxLength };
            var x = minx + Math.random() * size[0] * resolution;
            var y = miny + Math.random() * size[1] * resolution;
            var points = [], speeds_1 = [], distance = 0;
            var lastU = undefined, lastV = undefined;
            var existPoint = false;
            while (extent[0] < x && x < extent[2] && x < maxx
                && extent[1] < y && y < extent[3] && y < maxy) {
                var ca = (x - extent[0]) / x_offset; // son.***
                var ha = (y - extent[1]) / y_offset; // son.***
                var c = Math.floor(ca); // son.***
                var h = Math.floor(ha); // son.***
                var index = h * width + c;
                var u00 = this.uu_[index];
                var v00 = this.vv_[index];
                var u01 = this.uu_[index + 1];
                var v01 = this.vv_[index + 1];
                if (u00 !== -999 && v00 !== -999 && u01 !== -999 && v01 !== -999) {
                    index = index + width;
                    var u10 = this.uu_[index];
                    var v10 = this.vv_[index];
                    var u11 = this.uu_[index + 1];
                    var v11 = this.vv_[index + 1];
                    if (u10 !== -999 && v10 !== -999 && u11 !== -999 && v11 !== -999) {
                        var cf = ca - c; // son.***
                        var hf = ha - h; // son.***
                        var icf = 1.0 - cf, ihf = 1.0 - hf;
                        var u_t = u00 * icf + u01 * cf; // son.***
                        var v_t = v00 * icf + v01 * cf; // son.***
                        var u_b = u10 * icf + u11 * cf; // son.***
                        var v_b = v10 * icf + v11 * cf; // son.***
                        var u = u_t * ihf + u_b * hf; // son.***
                        var v = v_t * ihf + v_b * hf; // son.***
                        var speed = Math.sqrt(u * u + v * v);
                        if (speed > 0.1) {
                            if (existPoint) {
                                var x0 = x - points[points.length - 2];
                                var y0 = y - points[points.length - 1];
                                var d = Math.sqrt(x0 * x0 + y0 * y0);
                                distance = distance + d;
                                if (distance > pathMaxLength) {
                                    // console.log(`distance = ${distance}`)
                                    break;
                                }
                                var nomU = u / speed, nomV = v / speed;
                                // if (speed < 2) {
                                var dot = nomU * lastU + nomV * lastV;
                                if (dot > 0) { // cos(60 * Math.PI / 180)
                                    break;
                                }
                                // }
                                lastU = -nomU;
                                lastV = -nomV;
                            } // end if
                            var absU = Math.abs(u), absV = Math.abs(v);
                            ;
                            if (absU > absV) {
                                v = v / absU;
                                if (u > 0) {
                                    u = 1;
                                }
                                else {
                                    u = -1;
                                }
                            }
                            else {
                                u = u / absV;
                                if (v > 0) {
                                    v = 1;
                                }
                                else {
                                    v = -1;
                                }
                            }
                            var ox = u * stepx * speed;
                            var oy = v * stepy * speed;
                            points.push(x);
                            points.push(y);
                            x = x + ox;
                            y = y + oy;
                            speeds_1.push(speed);
                            existPoint = true;
                        }
                        else {
                            break;
                        }
                    }
                    else {
                        break;
                    }
                }
                else {
                    break;
                }
            }
            if (points.length > 2 && distance > stepx * 2) {
                path.points = points;
                path.speeds = speeds_1;
                path.distance = distance;
                this.paths_[i] = path;
            }
            else {
                this.paths_[i] = null;
            }
            // console.log("x = %f, y = %f, c = %f, h = %f, index = %f, u = %f, v = %f", x, y, c, h, index, this.uu_[index], this.vv_[index]);
        }
        var _loop_1 = function (i) {
            var path = this_1.paths_[i];
            if (path !== null) {
                // paths = paths.concat(path.points);
                path.points.forEach(function (p) { return paths.push(p); });
                totalLength = totalLength + path.points.length;
                elementsIndex.push(totalLength);
                starts.push(path.start);
                var cnt_1 = 0, speed_1 = 0;
                path.speeds.forEach(function (s) { speed_1 = speed_1 + s; cnt_1++; });
                speeds.push(speed_1 / cnt_1);
                // console.log(` speed = ${speed / cnt}`)
            }
        };
        var this_1 = this;
        // console.log(` WindyLayerRenderer -> rebuildBuffers_ (8) ${(new Date().getTime()) - st}`);
        for (var i = 0; i < this.paths_.length; i++) {
            _loop_1(i);
        }
        // console.log(`total points = ${elementsIndex[elementsIndex.length - 1]} `);
        // console.log(` WindyLayerRenderer -> rebuildBuffers_ end(10) ${(new Date().getTime()) - st}`);
        this.makeVertexs(paths, starts, speeds, elementsIndex);
    };
    NwpWindyLayerRenderer.prototype.setVertex = function (floatData, cursor, s, startPosition, side, speed) {
        // let cursor = vtxCursor * stride;
        floatData[cursor + 0] = s.current[0];
        floatData[cursor + 1] = s.current[1];
        floatData[cursor + 2] = s.offset[0] * side * this.thick_;
        floatData[cursor + 3] = s.offset[1] * side * this.thick_;
        floatData[cursor + 4] = s.distance;
        floatData[cursor + 5] = startPosition;
        floatData[cursor + 6] = side;
        floatData[cursor + 7] = speed;
    };
    NwpWindyLayerRenderer.prototype.makeVertexs = function (paths, starts, speeds, elementsIndex) {
        var stt = new Date().getTime();
        var vertexes = elementsIndex[elementsIndex.length - 1];
        var vertexBuffer = new ArrayBuffer(this.stride_ * vertexes * 4);
        var floatData = new Float32Array(vertexBuffer);
        var st = 0, ed, total = 0;
        for (var i = 0; i < elementsIndex.length; i++) {
            ed = elementsIndex[i];
            total = total + ((ed - st - 2) * 3);
            st = ed;
        }
        var indexData = new Uint32Array(total);
        var vtxCursor = 0, idxCursor = 0;
        st = 0;
        var deltaLength = 0;
        var maxOffset = [0, 0];
        for (var i = 0; i < elementsIndex.length; i++) {
            ed = elementsIndex[i];
            var startPosition = starts[i];
            // const age = ages[i];
            var speed = speeds[i];
            // Initialize new triangulation state.
            var s = {};
            // Process each vertex.
            for (var j = st; j < ed; j += 2) {
                // Point p is an original vertex of the polyline; we need to produce two extruded
                // GPU vertices, for each original vertex.
                var p = [paths[j], paths[j + 1]];
                if (s.current) {
                    // If this is not the first point, we compute the vector between the previous
                    // and the next vertex.
                    s.delta = [p[0] - s.current[0], p[1] - s.current[1]];
                    // And we normalize it. This is the direction of the current line segment
                    // that we are processing.
                    deltaLength = Math.sqrt(s.delta[0] * s.delta[0] + s.delta[1] * s.delta[1]);
                    s.direction = [s.delta[0] / deltaLength, s.delta[1] / deltaLength];
                    // We want to compute the normal to that segment. The normal of a
                    // vector (x, y) can be computed by rotating it by 90 degrees; this yields (-y, x).
                    var normal = [-s.direction[1], s.direction[0]];
                    if (s.normal) {
                        // If there is already a normal vector in the state, then the offset is the
                        // average of that normal and the next normal, i.e. the bisector of the turn.
                        s.offset = [s.normal[0] + normal[0], s.normal[1] + normal[1]];
                        // We first normalize it.
                        var offsetLength = Math.sqrt(s.offset[0] * s.offset[0] + s.offset[1] * s.offset[1]);
                        s.offset[0] /= offsetLength;
                        s.offset[1] /= offsetLength;
                        // Then we scale it like the cosine of the half turn angle. This can
                        // be computed as the dot product between the previous normal and the
                        // normalized bisector.
                        var d = s.normal[0] * s.offset[0] + s.normal[1] * s.offset[1];
                        s.offset[0] /= d;
                        s.offset[1] /= d;
                    }
                    else {
                        // Otherwise, this is the offset of the first vertex; it is equal to the
                        // normal we just computed.
                        s.offset = [normal[0], normal[1]];
                    }
                    maxOffset[0] = Math.max(maxOffset[0], Math.abs(s.offset[0]));
                    maxOffset[1] = Math.max(maxOffset[1], Math.abs(s.offset[1]));
                    // All the values that we computed are written to the first GPU vertex.
                    var cursor_1 = vtxCursor * this.stride_;
                    this.setVertex(floatData, cursor_1, s, startPosition, +1, speed);
                    // We also write the same values to the second vertex, but we negate the
                    // offset and the side (these are the attributes at positions +9, +10 and +12).
                    cursor_1 = cursor_1 + this.stride_;
                    this.setVertex(floatData, cursor_1, s, startPosition, -1, speed);
                    vtxCursor += 2;
                    if (j > st + 2) {
                        // If this is the third iteration then it means that we have emitted
                        // four GPU vertices already; we can form a triangle with them.
                        // console.log(` idxCursor = ${idxCursor}, vtxCursor = ${vtxCursor}, j = ${j}, st = ${st}`);
                        indexData[idxCursor + 0] = vtxCursor - 4;
                        indexData[idxCursor + 1] = vtxCursor - 3;
                        indexData[idxCursor + 2] = vtxCursor - 2;
                        indexData[idxCursor + 3] = vtxCursor - 3;
                        indexData[idxCursor + 4] = vtxCursor - 1;
                        indexData[idxCursor + 5] = vtxCursor - 2;
                        idxCursor += 6;
                    }
                    // The next normal becomes the current normal at the next iteration.
                    s.normal = normal;
                    // We increment the distance along the line by the length of the segment
                    // that we just processed.
                    s.distance += deltaLength;
                    // console.log(` s.distance = ${s.distance}`);
                }
                else {
                    s.distance = 0;
                }
                // We move to the next point.
                s.current = p;
            }
            // console.log(` s.distance = ${s.distance},  resolution = ${resolution}, startPosition = ${startPosition}`);
            // Finishing up (last 2 extruded vertices and 6 indices).
            var cursor = vtxCursor * this.stride_;
            s.offset = [s.normal[0], s.normal[1]];
            this.setVertex(floatData, cursor, s, startPosition, +1, speed);
            cursor = cursor + this.stride_;
            this.setVertex(floatData, cursor, s, startPosition, -1, speed);
            vtxCursor += 2;
            indexData[idxCursor + 0] = vtxCursor - 4;
            indexData[idxCursor + 1] = vtxCursor - 3;
            indexData[idxCursor + 2] = vtxCursor - 2;
            indexData[idxCursor + 3] = vtxCursor - 3;
            indexData[idxCursor + 4] = vtxCursor - 1;
            indexData[idxCursor + 5] = vtxCursor - 2;
            idxCursor += 6;
            // There is no next vertex.
            s.current = null;
            st = ed;
        }
        /*
        console.log(`maxOffset = ${maxOffset[0]}, ${maxOffset[1]}`);
        console.log(`${floatData.length} = ${floatData}`);
        console.log(`${elementsIndex.length} = ${elementsIndex}`);
        console.log(`${indexData.length} = ${indexData}`);
        
        for (let index = 0; index < floatData.length; index += this.stride_) {
            // console.log(`i = ${index / stride}, distance  = ${floatData[index] + 4} `);
            // console.log(`i = ${index / stride}, x = ${floatData[index]}, y = ${floatData[index + 1]}, ox = ${floatData[index + 2]}, oy = ${floatData[index + 3]}`);
            // console.log(`i = ${index / stride}, x = ${floatData[index] + (floatData[index + 2] * resolution)}, y = ${floatData[index + 1] + (floatData[index + 3] * resolution)}`);
            console.log(`i = ${index / this.stride_},  ${applyTransform(this.currentTransform_, [floatData[index], floatData[index + 1]])} `);
            // console.log(`i = ${index / stride},  ${[floatData[index + 2], floatData[index + 3]]} `);
        }
        */
        this.verticesBuffer_.fromArrayBuffer(floatData);
        this.helper.flushBufferData(this.verticesBuffer_);
        this.indicesBuffer_.fromArrayBuffer(indexData);
        this.helper.flushBufferData(this.indicesBuffer_);
        // this.getLayer().changed();
        // console.log(`this.stride_ = ${this.stride_}, vertices =  ${floatData.length}, indices =  ${indexData.length} `);
        // console.log(` makeVertexs script end(10) ${(new Date().getTime()) - stt}`);
    };
    /**
     * @param {import("../../coordinate.js").Coordinate} coordinate Coordinate.
     * @param {import("../../PluggableMap.js").FrameState} frameState Frame state.
     * @param {number} hitTolerance Hit tolerance in pixels.
     * @param {function(import("../../Feature.js").FeatureLike, import("../../layer/Layer.js").default): T} callback Feature callback.
     * @param {Array<import("../../Feature.js").FeatureLike>} declutteredFeatures Decluttered features.
     * @return {T|void} Callback result.
     * @template T
     */
    NwpWindyLayerRenderer.prototype.forEachFeatureAtCoordinate = function (coordinate, frameState, hitTolerance, callback, declutteredFeatures) {
        callback(null);
    };
    /**
     * Clean up.
     */
    NwpWindyLayerRenderer.prototype.disposeInternal = function () {
        this.layer_ = null;
        this.image_ = null;
        this.uu_ = null;
        this.vv_ = null;
        _super.prototype.disposeInternal.call(this);
    };
    return NwpWindyLayerRenderer;
}(WebGLLayerRenderer));
export default NwpWindyLayerRenderer;
//# sourceMappingURL=NwpWindyLayerRenderer.js.map